import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ReviewsSection from '../components/ReviewsSection';
import { Toaster } from '../components/ui/toaster';
import axios from 'axios';
import { ChevronLeft, ChevronRight, ShoppingBag, Star } from 'lucide-react';
import { resolveImageUrl } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [collectionProducts, setCollectionProducts] = useState({});
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [banners, setBanners] = useState([]);
  const [featuredBrands, setFeaturedBrands] = useState([]);
  const [shopCategories, setShopCategories] = useState([]);
  const [collections, setCollections] = useState([]);

  useEffect(() => {
    fetchBanners();
    fetchFeaturedBrands();
    fetchShopCategories();
    fetchCollections();
  }, []);

  useEffect(() => {
    if (banners.length > 0) {
      const interval = setInterval(() => {
        setCurrentBannerIndex((prev) => (prev + 1) % banners.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [banners]);

  const fetchBanners = async () => {
    try {
      const response = await axios.get(`${API}/banners?active_only=true`);
      if (response.data.length > 0) {
        setBanners(response.data);
      } else {
        // Use default banners if no banners in database
        setBanners([
          {
            title: "Timeless Style, Authentic Watches",
            subtitle: "Trusted Since 1975",
            image: "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=1920&h=800&fit=crop",
            cta_text: "Shop Now",
            cta_link: "/products"
          },
          {
            title: "Premium Watch Collections",
            subtitle: "40+ Authorized Brands",
            image: "https://images.unsplash.com/photo-1594576722512-582bcd46fba3?w=1920&h=800&fit=crop",
            cta_text: "Explore Brands",
            cta_link: "/products"
          },
          {
            title: "Exclusive Offers on Luxury Watches",
            subtitle: "Up to 30% Off",
            image: "https://images.unsplash.com/photo-1524805444758-089113d48a6d?w=1920&h=800&fit=crop",
            cta_text: "Shop Luxury",
            cta_link: "/products?collection=Luxury"
          },
        ]);
      }
    } catch (error) {
      console.error('Error fetching banners:', error);
      // Use default banners on error
      setBanners([
        {
          title: "Timeless Style, Authentic Watches",
          subtitle: "Trusted Since 1975",
          image: "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=1920&h=800&fit=crop",
          cta_text: "Shop Now",
          cta_link: "/products"
        }
      ]);
    }
  };

  const fetchShopCategories = async () => {
    try {
      const response = await axios.get(`${API}/shop-categories`);
      setShopCategories(response.data);
    } catch (error) {
      console.error('Error fetching shop categories:', error);
      // Use default categories on error
      setShopCategories([
        { name: 'Men', image: 'https://images.unsplash.com/photo-1524805444758-089113d48a6d?w=400&h=400&fit=crop', link: '/products/Men' },
        { name: 'Women', image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400&h=400&fit=crop', link: '/products/Women' },
        { name: 'Kids', image: 'https://images.unsplash.com/photo-1594534475808-b18fc33b045e?w=400&h=400&fit=crop', link: '/products/Kids' },
        { name: 'Smart Watches', image: 'https://images.unsplash.com/photo-1579586337278-3befd40fd17a?w=400&h=400&fit=crop', link: '/products/Smart Watches' },
      ]);
    }
  };

  const fetchCollections = async () => {
    try {
      const response = await axios.get(`${API}/collections`);
      const collectionsData = response.data;
      setCollections(collectionsData);
      
      // Fetch products for each active collection
      const productsPromises = collectionsData
        .filter(c => c.is_active)
        .map(collection => 
          axios.get(`${API}/products?collection=${collection.name}`)
            .then(res => ({ name: collection.name, products: res.data.slice(0, 4) }))
            .catch(() => ({ name: collection.name, products: [] }))
        );
      
      const productsResults = await Promise.all(productsPromises);
      const productsMap = {};
      productsResults.forEach(result => {
        productsMap[result.name] = result.products;
      });
      setCollectionProducts(productsMap);
    } catch (error) {
      console.error('Error fetching collections:', error);
      // Use default collections on error
      setCollections([
        { name: 'Best Selling', display_name: 'BEST SELLING', is_active: true },
        { name: 'Luxury', display_name: 'OUR LUXURY', is_active: true },
        { name: 'Best Budget', display_name: 'BEST BUDGET', is_active: true },
        { name: 'Astrological', display_name: 'ASTROLOGICAL WATCHES', is_active: true },
      ]);
    }
  };

  const fetchFeaturedBrands = async () => {
    try {
      const response = await axios.get(`${API}/featured-brands`);
      if (response.data.length > 0) {
        setFeaturedBrands(response.data);
      } else {
        // Default brands if none in database
        setFeaturedBrands([
          { name: "Casio", logo_url: "", link: "/products?brand=Casio" },
          { name: "Titan", logo_url: "", link: "/products?brand=Titan" },
          { name: "Fastrack", logo_url: "", link: "/products?brand=Fastrack" },
          { name: "Timex", logo_url: "", link: "/products?brand=Timex" },
        ]);
      }
    } catch (error) {
      console.error('Error fetching featured brands:', error);
      // Use default brands on error
      setFeaturedBrands([
        { name: "Casio", logo_url: "", link: "/products?brand=Casio" },
        { name: "Titan", logo_url: "", link: "/products?brand=Titan" },
        { name: "Fastrack", logo_url: "", link: "/products?brand=Fastrack" },
        { name: "Timex", logo_url: "", link: "/products?brand=Timex" },
      ]);
    }
  };

  const nextBanner = () => {
    setCurrentBannerIndex((prev) => (prev + 1) % banners.length);
  };

  const prevBanner = () => {
    setCurrentBannerIndex((prev) => (prev - 1 + banners.length) % banners.length);
  };

  const ProductCard = ({ product }) => (
    <Link
      to={`/product/${product.slug || product.id}`}
      className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
    >
      <div className="relative overflow-hidden aspect-[4/3] sm:aspect-square">
        <img
          src={resolveImageUrl(product.image)}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {product.original_price && (
          <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-0.5 rounded-full text-xs font-bold">
            {Math.round(((product.original_price - product.price) / product.original_price) * 100)}% OFF
          </div>
        )}
      </div>
      <div className="p-3 sm:p-4">
        <p className="text-xs text-yellow-600 font-semibold mb-0.5">{product.brand}</p>
        <h3 className="text-sm font-semibold text-gray-800 mb-1.5 line-clamp-2 h-10">{product.name}</h3>
        <div className="flex items-center mb-1.5">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
          ))}
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-base sm:text-lg font-bold text-gray-900">₹{product.price.toLocaleString()}</p>
            {product.original_price && (
              <p className="text-xs text-gray-500 line-through">₹{product.original_price.toLocaleString()}</p>
            )}
          </div>
          <button className="bg-yellow-500 hover:bg-yellow-600 text-black px-2 sm:px-3 py-1.5 rounded-full transition-colors duration-300 flex items-center space-x-1">
            <ShoppingBag className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="text-xs font-semibold hidden sm:inline">Add</span>
          </button>
        </div>
      </div>
    </Link>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Banner Carousel - Reduced height on mobile */}
      <div className="relative h-[280px] sm:h-[400px] md:h-[500px] overflow-hidden">
        {banners.map((banner, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentBannerIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div className="relative h-full">
              <img
                src={resolveImageUrl(banner.image)}
                alt={banner.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent">
                <div className="container mx-auto px-4 h-full flex items-center">
                  <div className="max-w-2xl text-white">
                    <p className="text-yellow-500 text-sm sm:text-base md:text-lg font-semibold mb-1 sm:mb-2">{banner.subtitle}</p>
                    <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-3 sm:mb-4 md:mb-6 leading-tight">{banner.title}</h1>
                    <Link
                      to={banner.cta_link}
                      className="inline-block bg-yellow-500 hover:bg-yellow-600 text-black px-5 sm:px-6 md:px-8 py-2 sm:py-3 md:py-4 rounded-full text-sm sm:text-base md:text-lg font-bold transition-all duration-300 transform hover:scale-105"
                    >
                      {banner.cta_text}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
        <button
          onClick={prevBanner}
          className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-2 sm:p-3 rounded-full transition-all"
        >
          <ChevronLeft className="w-4 h-4 sm:w-6 sm:h-6" />
        </button>
        <button
          onClick={nextBanner}
          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-2 sm:p-3 rounded-full transition-all"
        >
          <ChevronRight className="w-4 h-4 sm:w-6 sm:h-6" />
        </button>
        <div className="absolute bottom-3 sm:bottom-4 left-1/2 -translate-x-1/2 flex space-x-1.5 sm:space-x-2">
          {banners.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentBannerIndex(index)}
              className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all ${
                index === currentBannerIndex ? 'bg-yellow-500 w-5 sm:w-8' : 'bg-white/50'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Featured Brands - Authorized Retailers */}
      <div className="bg-white py-6 sm:py-8 md:py-12">
        <div className="container mx-auto px-4">
          <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-center mb-4 sm:mb-6 md:mb-8">AUTHORIZED RETAILERS FOR 40+ BRANDS</h2>
          <div className="overflow-x-auto scrollbar-hide">
            <div className="flex space-x-4 sm:space-x-6 md:space-x-8 pb-4 min-w-min">
              {featuredBrands.map((brand, idx) => (
                <Link
                  key={brand.id || idx}
                  to={brand.link || `/products?brand=${brand.name}`}
                  className="flex-shrink-0 flex flex-col items-center justify-center p-3 sm:p-4 md:p-6 hover:scale-110 transition-transform duration-300 min-w-[80px] sm:min-w-[100px] md:min-w-[140px]"
                >
                  {brand.logo_url ? (
                    <img 
                      src={resolveImageUrl(brand.logo_url)} 
                      alt={brand.name} 
                      className="h-10 sm:h-12 md:h-16 w-auto object-contain mb-2"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                  ) : null}
                  <div className={`text-xs sm:text-sm md:text-lg lg:text-xl font-bold text-gray-800 text-center ${brand.logo_url ? '' : ''}`}>
                    {brand.name}
                  </div>
                </Link>
              ))}
            </div>
          </div>
          {featuredBrands.length > 4 && (
            <p className="text-center text-xs sm:text-sm text-gray-500 mt-2">← Scroll to see more brands →</p>
          )}
        </div>
      </div>

      {/* Category Section - Dynamic */}
      {shopCategories.length > 0 && (
        <div className="container mx-auto px-4 py-8 sm:py-12 md:py-16">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-6 sm:mb-8 md:mb-12">SHOP BY CATEGORY</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
            {shopCategories.filter(c => c.is_active !== false).map((category, idx) => (
              <Link
                key={category.id || idx}
                to={category.link || `/products/${category.name}`}
                className="group relative overflow-hidden rounded-lg aspect-square shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <img
                  src={resolveImageUrl(category.image)}
                  alt={category.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent flex items-end justify-center pb-3 sm:pb-4 md:pb-6">
                  <h3 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-white">{category.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Dynamic Collection Sections */}
      {collections.filter(c => c.is_active !== false).map((collection, idx) => (
        <div key={collection.id || idx} className={idx % 2 === 0 ? "bg-white py-8 sm:py-12 md:py-16" : "container mx-auto px-4 py-8 sm:py-12 md:py-16"}>
          <div className={idx % 2 === 0 ? "container mx-auto px-4" : ""}>
            <div className="flex justify-between items-center mb-4 sm:mb-6 md:mb-8">
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold">{collection.display_name || collection.name.toUpperCase()}</h2>
              <Link to={`/products?collection=${collection.name}`} className="text-sm sm:text-base text-yellow-600 hover:text-yellow-700 font-semibold">
                View All →
              </Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
              {(collectionProducts[collection.name] || []).map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </div>
      ))}

      {/* Website Reviews Section */}
      <div className="container mx-auto px-3 sm:px-4 pb-8 sm:pb-12" data-testid="website-reviews-container">
        <ReviewsSection
          productId="__website__"
          productName="Jain Watch Company"
          title="Website Reviews"
          subtitle="Share your experience shopping with Jain Watch Company"
          scrollable
        />
      </div>
      <Toaster />

      <Footer />
    </div>
  );
};

export default Home;
